import { WORKSPACE_DOCUMENT_LIMITS } from './document.mjs';

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const stale = () => new Error('선택한 페이지나 항목이 바뀌었습니다. 현재 항목을 확인한 뒤 다시 적용해 주세요.');
const plainText = delta => delta.map(part => part.insert).join('');

// A plain-text edit replaces only its changed range. Existing rich text on
// either side keeps its attributes, and inserted text inherits the nearby run.
function replaceText(delta, text) {
  const original = plainText(delta), old = Array.from(original), next = Array.from(text);
  let start = 0, end = 0;
  while (start < old.length && start < next.length && old[start] === next[start]) start++;
  while (end < old.length - start && end < next.length - start && old[old.length - end - 1] === next[next.length - end - 1]) end++;
  const before = old.slice(0, start).join('').length;
  const after = old.slice(0, old.length - end).join('').length;
  const result = [];
  let offset = 0, attributes;
  const push = (insert, style) => {
    if (insert) result.push({ insert, ...(style ? { attributes: structuredClone(style) } : {}) });
  };
  for (const part of delta) {
    const limit = offset + part.insert.length;
    if (offset <= before && (before < limit || before === limit && limit === original.length)) attributes = part.attributes;
    if (offset < before) push(part.insert.slice(0, Math.min(part.insert.length, before - offset)), part.attributes);
    offset = limit;
  }
  push(next.slice(start, next.length - end).join(''), attributes);
  offset = 0;
  for (const part of delta) {
    if (offset + part.insert.length > after) push(part.insert.slice(Math.max(0, after - offset)), part.attributes);
    offset += part.insert.length;
  }
  return result;
}

/** Connect the shared content panel to the current native BlockSuite page. */
export function createBlockSuiteContentSession({ editor, fileId, pageId }) {
  if (!editor || typeof fileId !== 'string' || !fileId || typeof pageId !== 'string' || !pageId) {
    throw new Error('캔버스 콘텐츠 편집기의 문서와 페이지가 필요합니다.');
  }
  let disposed = false;
  const active = () => { if (disposed) throw stale(); };
  const scope = input => {
    active();
    if (input.fileId !== fileId || input.pageId !== pageId) throw stale();
  };
  const selected = id => {
    const selection = editor.getSelection();
    if (selection.length !== 1 || selection[0].id !== id) throw stale();
  };
  const items = () => editor.getLayers().filter(layer => ['text', 'image'].includes(layer.type)).map(layer => {
    const editable = !layer.locked && layer.editableProperties.length > 0;
    const item = { id: layer.id, name: layer.label, type: layer.type, editable,
      ...(!editable ? { reason: '잠겨 있거나 읽기 전용인 항목입니다.' } : {}) };
    if (layer.type === 'text') {
      const text = editor.getText(layer.id);
      if (!text) throw new Error('캔버스의 글 원본을 읽지 못했습니다.');
      item.text = plainText(text.delta);
      if (text.locked) { item.editable = false; item.reason = '잠겨 있는 글입니다.'; }
    } else item.image = { name: layer.label, width: layer.width, height: layer.height };
    return item;
  });
  const content = () => ({ fileId, pageId, items: items(), selection: editor.getSelection().map(layer => layer.id) });
  return {
    getStatus() { return { connected: !disposed, selection: disposed ? [] : editor.getSelection().map(layer => layer.id) }; },
    async execute(operation, input = {}) {
      active();
      if (operation === 'content_items') return content();
      if (!['select_content', 'update_content'].includes(operation)) throw new Error(`지원하지 않는 콘텐츠 명령: ${operation}`);
      scope(input);
      const item = items().find(value => value.id === input.id);
      if (!item) throw stale();
      if (operation === 'select_content') { editor.selectElements([item.id]); return content(); }
      selected(item.id);
      if (!item.editable) throw new Error('잠겨 있거나 읽기 전용인 항목은 고칠 수 없습니다.');
      if (item.type === 'text') {
        if (typeof input.text !== 'string' || typeof input.expectedText !== 'string' || Object.hasOwn(input, 'image')) {
          throw new Error('바꿀 글과 원본 글을 확인해 주세요.');
        }
        const current = editor.getText(item.id);
        if (plainText(current.delta) !== input.expectedText) throw new Error('원본 글이 변경되어 적용하지 않았습니다. 입력한 내용을 확인해 주세요.');
        if (input.text !== input.expectedText) editor.setTextContent(item.id, replaceText(current.delta, input.text));
      } else {
        const image = input.image;
        if (Object.hasOwn(input, 'text') || !image || typeof image.name !== 'string' || !IMAGE_TYPES.has(image.mime) || typeof image.data !== 'string') {
          throw new Error('PNG, JPEG, WebP, GIF 이미지 원본을 선택해 주세요.');
        }
        let bytes;
        try { bytes = Uint8Array.from(atob(image.data), value => value.charCodeAt(0)); }
        catch { throw new Error('이미지 원본 데이터가 올바르지 않습니다.'); }
        if (!bytes.length || bytes.length > WORKSPACE_DOCUMENT_LIMITS.assetBytes) {
          throw new Error(`${WORKSPACE_DOCUMENT_LIMITS.assetBytes / 1024 / 1024}MiB 이하의 이미지를 선택해 주세요.`);
        }
        await editor.replaceImage(item.id, new Blob([bytes], { type: image.mime }), {
          name: image.name, beforeApply: () => { active(); selected(item.id); },
        });
        active();
      }
      return { updated: true, ...content() };
    },
    dispose() { disposed = true; },
  };
}
